/**
 * useAuth Hook
 * 
 * Manages authentication state and session lifecycle:
 * - Session initialization and verification on app load
 * - Email login
 * - Google OAuth sign-in and callback handling
 * - Logout
 * - Token/session storage
 * 
 * Exposes `authReady` flag that becomes true ONLY after:
 * 1. Session check is complete (verified or no session)
 * 2. Ready for entitlements/tier loading
 * 
 * This hook does NOT manage:
 * - Entitlements/subscription logic (stays in useSubscription)
 * - Feature state (meals, exercises, goals)
 * - UI state
 */

import { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import authService from '../services/authService';
import { debug } from '../utils/debug';
import { track, EVENTS, identifyUser, resetUser } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Safe localStorage helpers (handle Safari private browsing)
const safeGetItem = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    console.warn('[useAuth] localStorage.getItem failed:', e);
    return null;
  }
};

const safeRemoveItem = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.warn('[useAuth] localStorage.removeItem failed:', e);
  }
};

export const useAuth = () => {
  // ═══════════════════════════════════════════════════════════════════════════════
  // AUTH STATE
  // ═══════════════════════════════════════════════════════════════════════════════
  const [userId, setUserId] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false); // True only after session resolved
  const [authError, setAuthError] = useState('');
  const [isGuestMode, setIsGuestMode] = useState(false);
  
  // Form state for login/signup UI (kept here for convenience)
  const [authView, setAuthView] = useState('login'); // 'login', 'signup'
  const [authForm, setAuthForm] = useState({ email: '', password: '', name: '' });
  
  // OAuth session handling ref
  const oauthSessionIdRef = useRef(null);
  const isProcessingOAuth = useRef(false); // Prevent duplicate OAuth processing
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // CAPTURE OAUTH SESSION_ID BEFORE REACT HYDRATION
  // ═══════════════════════════════════════════════════════════════════════════════
  useLayoutEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const hashParams = new URLSearchParams(hash.substring(1));
      const sessionId = hashParams.get('session_id');
      if (sessionId) {
        oauthSessionIdRef.current = sessionId;
        // Clear hash immediately to prevent re-processing
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, []);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // SESSION INITIALIZATION - Check existing session on mount
  // ═══════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    const checkExistingSession = async () => {
      console.log('[BOOT] Auth check start');
      debug.auth('[useAuth] Checking existing session...');
      
      // Check for reset parameter - allows fresh preview with ?reset=true
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('reset') === 'true') {
        debug.log('[useAuth] Clearing all local data for fresh preview');
        safeRemoveItem('guest_mode_data');
        authService.clearStoredToken();
        authService.clearStoredUserId();
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      
      const sessionToken = authService.getStoredToken();
      console.log('[BOOT] Session token:', sessionToken ? 'present' : 'missing');
      debug.auth('[useAuth] Session token:', sessionToken ? 'present' : 'missing');
      
      if (sessionToken) {
        try {
          console.log('[BOOT] Verifying session...');
          const userData = await authService.verifySession(sessionToken);
          console.log('[BOOT] Session verified, user:', userData.user_id);
          debug.auth('[useAuth] Session valid for user:', userData.user_id);
          
          setAuthUser(userData);
          setIsAuthenticated(true);
          setUserId(userData.user_id);
          setIsGuestMode(false);
          authService.setStoredUserId(userData.user_id);
          
          // Identify user for analytics
          identifyUser(userData.user_id, {
            email: userData.email,
            auth_provider: userData.auth_provider || 'email',
            created_at: userData.created_at
          });
          
        } catch (error) {
          console.log('[BOOT] Session invalid:', error.message);
          debug.auth('[useAuth] Session invalid, clearing:', error.message);
          authService.clearStoredToken();
          authService.clearStoredUserId();
          setIsGuestMode(true);
        }
      } else {
        console.log('[BOOT] No session, guest mode');
        debug.auth('[useAuth] No session token, entering guest mode');
        setIsGuestMode(true);
      }
      
      // Mark auth as ready - session check is complete
      console.log('[BOOT] Auth resolved, setting authReady=true');
      setAuthLoading(false);
      setAuthReady(true);
    };
    
    checkExistingSession();
  }, []);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // GOOGLE OAUTH CALLBACK HANDLER
  // ═══════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    // Priority 1: Check sessionStorage (set by index.html script before React loads)
    let sessionId = sessionStorage.getItem('oauth_session_id');
    if (sessionId) {
      sessionStorage.removeItem('oauth_session_id');
    }
    
    // Priority 2: Check ref (stored by useLayoutEffect)
    if (!sessionId) {
      sessionId = oauthSessionIdRef.current;
      oauthSessionIdRef.current = null;
    }
    
    // Priority 3: Check query params as fallback
    if (!sessionId) {
      const urlParams = new URLSearchParams(window.location.search);
      sessionId = urlParams.get('session_id');
      if (sessionId) {
        // Clear URL immediately
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
    
    // Priority 4: Check current hash
    if (!sessionId) {
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        const hashParams = new URLSearchParams(hash.substring(1));
        sessionId = hashParams.get('session_id');
        window.history.replaceState({}, document.title, window.location.pathname);
        window.scrollTo(0, 0);
      }
    }
    
    if (sessionId) {
      // Prevent duplicate processing
      if (isProcessingOAuth.current) {
        debug.auth('[useAuth] OAuth already processing, skipping duplicate');
        return;
      }
      isProcessingOAuth.current = true;
      
      const handleGoogleCallback = async () => {
        debug.auth('[useAuth] Processing Google OAuth callback...');
        
        try {
          const response = await fetch(`${BACKEND_URL}/api/auth/google/session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_id: sessionId })
          });
          
          if (response.ok) {
            const userData = await response.json();
            debug.auth('[useAuth] Google auth successful:', userData.user_id);
            
            authService.setStoredToken(userData.session_token);
            authService.setStoredUserId(userData.user_id);
            
            setAuthUser(userData);
            setIsAuthenticated(true);
            setIsGuestMode(false);
            setUserId(userData.user_id);
            
            // Clear URL and scroll to top
            window.history.replaceState({}, document.title, window.location.pathname);
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
            setTimeout(() => {
              window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
            }, 200);
            
            // Identify user for analytics
            identifyUser(userData.user_id, {
              email: userData.email,
              auth_provider: 'google',
              created_at: userData.created_at
            });
            
            // Track login event
            track(EVENTS.USER_LOGGED_IN, { auth_provider: 'google' });
            
          } else {
            const errorData = await response.json();
            console.error('[useAuth] Google auth error:', errorData);
            setAuthError(errorData.detail || 'Google sign-in failed. Please try again.');
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        } catch (error) {
          console.error('[useAuth] Google auth error:', error);
          setAuthError('Google sign-in failed. Please try again.');
          window.history.replaceState({}, document.title, window.location.pathname);
        } finally {
          isProcessingOAuth.current = false;
        }
        
        setAuthLoading(false);
        setAuthReady(true);
      };
      
      handleGoogleCallback();
    }
  }, []);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // EMAIL LOGIN
  // ═══════════════════════════════════════════════════════════════════════════════
  const handleLogin = useCallback(async (e) => {
    if (e) e.preventDefault();
    setAuthError('');
    
    try {
      const responseData = await authService.login(authForm.email, authForm.password);
      debug.auth('[useAuth] Login successful:', responseData.user_id);
      
      authService.setStoredToken(responseData.session_token);
      authService.setStoredUserId(responseData.user_id);
      
      setAuthUser(responseData);
      setIsAuthenticated(true);
      setIsGuestMode(false);
      setUserId(responseData.user_id);
      setAuthForm({ email: '', password: '', name: '' });
      
      // Force scroll to top
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      
      // Track login event
      track(EVENTS.USER_LOGGED_IN, { auth_provider: 'email' });
      
      // Identify user for analytics
      identifyUser(responseData.user_id, {
        email: responseData.email,
        auth_provider: 'email'
      });
      
      return { success: true, userId: responseData.user_id, userData: responseData };
      
    } catch (error) {
      debug.auth('[useAuth] Login error:', error.message);
      setAuthError(error.message || 'Login failed. Please check your credentials.');
      return { success: false, error: error.message };
    }
  }, [authForm.email, authForm.password]);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // EMAIL SIGNUP
  // ═══════════════════════════════════════════════════════════════════════════════
  const handleSignup = useCallback(async (e) => {
    if (e) e.preventDefault();
    setAuthError('');
    
    try {
      const responseData = await authService.signup(
        authForm.email,
        authForm.password,
        authForm.name
      );
      debug.auth('[useAuth] Signup successful:', responseData.user_id);
      
      authService.setStoredToken(responseData.session_token);
      authService.setStoredUserId(responseData.user_id);
      
      setAuthUser(responseData);
      setIsAuthenticated(true);
      setIsGuestMode(false);
      setUserId(responseData.user_id);
      setAuthForm({ email: '', password: '', name: '' });
      
      // Force scroll to top
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      
      // Track signup event
      track(EVENTS.USER_SIGNED_UP, {
        auth_provider: 'email',
        had_guest_meals: Boolean(safeGetItem('guest_meals'))
      });
      
      // Identify user for analytics
      identifyUser(responseData.user_id, {
        email: responseData.email,
        auth_provider: 'email'
      });
      
      return { success: true, userId: responseData.user_id, userData: responseData };
      
    } catch (error) {
      debug.auth('[useAuth] Signup error:', error.message);
      setAuthError(error.message || 'Signup failed. Please try again.');
      return { success: false, error: error.message };
    }
  }, [authForm.email, authForm.password, authForm.name]);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // GOOGLE SIGN-IN INITIATION
  // ═══════════════════════════════════════════════════════════════════════════════
  const handleGoogleSignIn = useCallback(() => {
    // Use the browser's location to ensure correct domain redirect
    const redirectUrl = window.location.origin;
    // prompt=select_account forces account picker, preventing silent reuse of old sessions
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&prompt=select_account`;
  }, []);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // LOGOUT
  // ═══════════════════════════════════════════════════════════════════════════════
  const handleLogout = useCallback(async () => {
    debug.auth('[useAuth] Logging out...');
    
    // Track logout event before resetting
    track(EVENTS.USER_LOGGED_OUT);
    resetUser();
    
    // Call backend logout
    const sessionToken = authService.getStoredToken();
    await authService.logout(sessionToken);
    
    // Clear storage
    authService.clearStoredToken();
    authService.clearStoredUserId();
    
    // Reset state
    setIsAuthenticated(false);
    setAuthUser(null);
    setUserId(null);
    setIsGuestMode(true);
    setAuthError('');
    
    debug.auth('[useAuth] Logout complete');
  }, []);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // CLEAR AUTH ERROR
  // ═══════════════════════════════════════════════════════════════════════════════
  const clearAuthError = useCallback(() => {
    setAuthError('');
  }, []);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // RETURN HOOK VALUES
  // ═══════════════════════════════════════════════════════════════════════════════
  return {
    // Core auth state
    userId,
    authUser,
    isAuthenticated,
    authLoading,
    authReady,        // True only after session is resolved
    authError,
    isGuestMode,
    
    // Form state (for auth UI)
    authView,
    setAuthView,
    authForm,
    setAuthForm,
    
    // Actions
    handleLogin,
    handleSignup,
    handleGoogleSignIn,
    handleLogout,
    clearAuthError,
    
    // State setters (for edge cases where App.js needs to update)
    setUserId,
    setAuthUser,
    setIsAuthenticated,
    setIsGuestMode,
    setAuthError,
    setAuthReady
  };
};

export default useAuth;
